import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Bitte geben Sie Ihren Namen an.";
    if (!form.phone.trim() || form.phone.trim().length < 4)
      e.phone = "Bitte geben Sie eine gültige Telefonnummer an.";
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    if (!form.message.trim() || form.message.trim().length < 3)
      e.message = "Bitte beschreiben Sie kurz Ihr Anliegen.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Anfrage gesendet — wir melden uns in Kürze!");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.";
      toast.error(typeof msg === "string" ? msg : "Fehler beim Senden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="max-w-2xl mb-14 md:mb-20">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
            <span className="w-6 h-px bg-sky-600" /> Kontakt
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-slate-900 leading-[1.05]">
            Kontaktieren Sie uns.
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
            Sie möchten ein Angebot oder haben Fragen zu unseren Leistungen?
            Schreiben Sie uns — wir antworten schnell und unkompliziert.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Info panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 rounded-2xl bg-slate-950 text-white p-8 md:p-10 relative overflow-hidden"
          >
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-sky-500/20 blur-3xl" />
            <div className="relative">
              <h3 className="font-display text-2xl font-semibold">
                {SITE.name}
              </h3>
              <p className="mt-2 text-slate-300 text-sm">
                Familiengeführte Gebäudereinigung in Bayern und Baden-Württemberg.
              </p>

              <div className="mt-8 space-y-5">
                <InfoRow
                  icon={MapPin}
                  label="Adresse"
                  value={SITE.address}
                />
                <InfoRow
                  icon={Phone}
                  label="Telefon"
                  value={SITE.phone}
                  href={SITE.phoneHref}
                  testid="contact-phone"
                />
                <InfoRow
                  icon={Mail}
                  label="E-Mail"
                  value={SITE.email}
                  href={SITE.emailHref}
                  testid="contact-email"
                />
                <InfoRow
                  icon={Clock}
                  label="Öffnungszeiten"
                  value={
                    <>
                      <div>{SITE.hoursWeek}</div>
                      <div className="text-slate-400">{SITE.hoursSat}</div>
                    </>
                  }
                />
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-8 md:p-10 shadow-sm"
          >
            {submitted ? (
              <div
                data-testid="contact-success"
                className="min-h-[420px] flex flex-col items-center justify-center text-center"
              >
                <div className="grid place-items-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 mb-5">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-slate-900">
                  Vielen Dank für Ihre Anfrage!
                </h3>
                <p className="mt-3 text-slate-600 max-w-md">
                  Wir haben Ihre Nachricht erhalten und melden uns zeitnah bei Ihnen.
                </p>
                <Button
                  data-testid="contact-new-request"
                  variant="outline"
                  onClick={() => setSubmitted(false)}
                  className="mt-7 rounded-full"
                >
                  Weitere Anfrage senden
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    id="name"
                    label="Name"
                    value={form.name}
                    onChange={update("name")}
                    error={errors.name}
                    placeholder="Max Mustermann"
                    testid="contact-field-name"
                  />
                  <Field
                    id="phone"
                    label="Telefonnummer"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    error={errors.phone}
                    placeholder="0176 12345678"
                    testid="contact-field-phone"
                  />
                </div>
                <Field
                  id="email"
                  label="E-Mail"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  error={errors.email}
                  placeholder="ihre@email.de"
                  testid="contact-field-email"
                />
                <div>
                  <Label
                    htmlFor="message"
                    className="text-sm font-medium text-slate-800"
                  >
                    Nachricht
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-field-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Beschreiben Sie kurz Ihr Anliegen, das Objekt und gewünschte Leistungen."
                    className="mt-1.5 resize-none border-slate-200 focus-visible:ring-sky-500 focus-visible:ring-offset-0"
                  />
                  {errors.message && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.message}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="w-full md:w-auto rounded-full bg-sky-600 hover:bg-sky-700 text-white h-12 px-8 text-[15px] shadow-lg shadow-sky-600/25 transition-transform hover:-translate-y-0.5"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet…
                    </>
                  ) : (
                    <>
                      Anfrage senden <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500">
                  Mit dem Absenden akzeptieren Sie die Verarbeitung Ihrer Angaben zur
                  Beantwortung Ihrer Anfrage.
                </p>
              </form>
            )}
          </motion.div>
        </div>

        {/* Google Maps */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.12 }}
          data-testid="contact-map"
          className="mt-8 rounded-2xl overflow-hidden border border-slate-200 shadow-sm"
        >
          <iframe
            title="Standort Kola Gebäudereinigung auf Google Maps"
            src="https://www.google.com/maps?q=Augsburger+Stra%C3%9Fe+26%2C+86157+Augsburg%2C+Deutschland&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, display: "block" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, href, testid }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      data-testid={testid}
      className={`flex items-start gap-4 group ${href ? "hover:text-sky-300 transition-colors" : ""}`}
    >
      <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-sky-300">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-medium">
          {label}
        </div>
        <div className="mt-0.5 text-white text-[15px] leading-snug">{value}</div>
      </div>
    </Wrapper>
  );
}

function Field({ id, label, value, onChange, error, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-slate-800">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1.5 h-11 border-slate-200 focus-visible:ring-sky-500 focus-visible:ring-offset-0"
      />
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}
